/**
 * Pure parent-child lock derivation for admin-locked-assessments (Part 2).
 *
 * The backend stores locks as `(guide_level, school)` pairs; a lock on a guide level covers that
 * level and everything beneath it. The frontend owns the effective-state derivation and the
 * decomposition needed to add/remove locks while keeping siblings consistent (spec part-02 ac.md:40–45).
 * These helpers are pure (tree + one school's directly-locked ids) so they're unit-testable and shared
 * by the indicator (display) and the modal (seeding + confirm).
 */

export interface GuideLevelNode {
  id: string;
  parentId?: string | null;
}

export interface GuideTree {
  byId: Map<string, GuideLevelNode>;
  childIdsByParent: Map<string, string[]>;
}

export const buildGuideTree = (nodes: GuideLevelNode[]): GuideTree => {
  const byId = new Map<string, GuideLevelNode>();
  nodes.forEach((node) => byId.set(node.id, node));

  const childIdsByParent = new Map<string, string[]>();
  nodes.forEach((node) => {
    const parentId = node.parentId;
    if (parentId && byId.has(parentId)) {
      const children = childIdsByParent.get(parentId) ?? [];
      children.push(node.id);
      childIdsByParent.set(parentId, children);
    }
  });

  return {byId, childIdsByParent};
};

/** Ancestor ids, nearest first (parent → … → root). Only includes ancestors present in the tree. */
export const getAncestorIds = (tree: GuideTree, id: string): string[] => {
  const ancestors: string[] = [];
  const visited = new Set<string>([id]); // guards against a malformed cyclic parentId chain
  let current = tree.byId.get(id)?.parentId ?? null;
  while (current && tree.byId.has(current) && !visited.has(current)) {
    visited.add(current);
    ancestors.push(current);
    current = tree.byId.get(current)?.parentId ?? null;
  }
  return ancestors;
};

export const getChildIds = (tree: GuideTree, id: string): string[] =>
  tree.childIdsByParent.get(id) ?? [];

export const getDescendantIds = (tree: GuideTree, id: string): string[] => {
  const descendants: string[] = [];
  const visited = new Set<string>([id]); // guards against malformed cyclic/duplicate child links
  const stack = [...getChildIds(tree, id)];
  while (stack.length > 0) {
    const next = stack.pop() as string;
    if (visited.has(next)) {
      continue;
    }
    visited.add(next);
    descendants.push(next);
    stack.push(...getChildIds(tree, next));
  }
  return descendants;
};

const isEffectivelyLockedImpl = (
  tree: GuideTree,
  id: string,
  lockedIds: Set<string>,
  visited: Set<string>
): boolean => {
  if (lockedIds.has(id)) {
    return true;
  }
  if (getAncestorIds(tree, id).some((ancestorId) => lockedIds.has(ancestorId))) {
    return true;
  }
  // Already on the current path → a malformed cycle; break it rather than recurse forever.
  if (visited.has(id)) {
    return false;
  }
  visited.add(id);
  const childIds = getChildIds(tree, id);
  return (
    childIds.length > 0 &&
    childIds.every((childId) => isEffectivelyLockedImpl(tree, childId, lockedIds, visited))
  );
};

/**
 * Whether a guide level is effectively locked for a school, given that school's directly-locked ids:
 * it is directly locked, OR a (direct) ancestor is locked (cascade down), OR it has children and they
 * are all effectively locked (all-children locked rolls up to the parent).
 */
export const isEffectivelyLocked = (
  tree: GuideTree,
  id: string,
  lockedIds: Set<string>
): boolean => isEffectivelyLockedImpl(tree, id, lockedIds, new Set());

export interface LockChange {
  toLock: string[];
  toUnlock: string[];
}

export const computeLockChange = (
  tree: GuideTree,
  id: string,
  lockedIds: Set<string>
): LockChange => {
  const redundantDescendantLocks = getDescendantIds(tree, id).filter((descendantId) =>
    lockedIds.has(descendantId)
  );
  // A new lock on `id` is redundant if `id` (or a direct ancestor) is already locked — that lock
  // already covers `id` and its subtree. Redundant descendant locks are dropped either way.
  const alreadyCovered =
    lockedIds.has(id) || getAncestorIds(tree, id).some((ancestorId) => lockedIds.has(ancestorId));
  return {toLock: alreadyCovered ? [] : [id], toUnlock: redundantDescendantLocks};
};

/**
 * Unlocking a guide level — the three-level-aware decomposition (ac.md:41–44):
 *  - Remove every direct lock within the level's subtree.
 *  - If the level was locked only because an ancestor is locked, remove that ancestor lock (and any
 *    above it) and re-create locks on every off-path child along the way, so siblings stay locked.
 */
export const computeUnlockChange = (
  tree: GuideTree,
  id: string,
  lockedIds: Set<string>
): LockChange => {
  const toLock = new Set<string>();
  const toUnlock = new Set<string>();

  [id, ...getDescendantIds(tree, id)].forEach((nodeId) => {
    if (lockedIds.has(nodeId)) {
      toUnlock.add(nodeId);
    }
  });

  const ancestors = getAncestorIds(tree, id); // nearest first
  const lockedAncestorIndexes = ancestors
    .map((ancestorId, index) => (lockedIds.has(ancestorId) ? index : -1))
    .filter((index) => index >= 0);

  if (lockedAncestorIndexes.length > 0) {
    // Decompose every ancestor lock covering this level, from the level up to the highest locked one.
    const highestLockedIndex = Math.max(...lockedAncestorIndexes);
    const pathAncestors = ancestors.slice(0, highestLockedIndex + 1);
    const pathSet = new Set<string>([id, ...pathAncestors]);

    pathAncestors.forEach((ancestorId) => {
      if (lockedIds.has(ancestorId)) {
        toUnlock.add(ancestorId);
      }
      // Re-lock the children not on the path to `id` so they stay locked.
      getChildIds(tree, ancestorId).forEach((childId) => {
        if (!pathSet.has(childId)) {
          toLock.add(childId);
        }
      });
    });
  }

  return {
    toLock: [...toLock].filter((lockId) => !lockedIds.has(lockId)),
    toUnlock: [...toUnlock]
  };
};
